import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { memo, useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from '@eruvaka/web-pondlogs-i18n';
import { colorTokens } from '@eruvaka/web-pondlogs-base';
import { activatePond, updatePond, toggleTestPond, toggleAlerts } from '../api/pondsApi';
/* ── Brand palette ── */
const SPECIES = ['Vannamei', 'Monodon', 'Fish', 'Other'];
/* ── Form field helpers ── */
const FieldRow = ({ children }) => {
    return _jsx(Box, { sx: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }, children: children });
};
const Field = ({ label, required, children, }) => {
    return (_jsxs(Box, { children: [_jsxs(Typography, { sx: {
                    fontSize: 12,
                    fontWeight: 600,
                    color: colorTokens.primary,
                    mb: 0.75,
                    display: 'block',
                }, children: [label, required && (_jsxs(Box, { component: "span", sx: { color: colorTokens.error }, children: [' ', "*"] }))] }), children] }));
};
const inputSx = {
    width: '100%',
    px: 1.5,
    py: 1,
    borderRadius: '10px',
    fontSize: 13,
    border: `1px solid ${colorTokens.gray200}`,
    outline: 'none',
    color: colorTokens.gray800,
    bgcolor: colorTokens.white,
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    '&:focus': { borderColor: colorTokens.primary },
    '&:disabled': { bgcolor: colorTokens.gray50, color: colorTokens.gray600 },
};
const Toggle = ({ checked, onChange }) => {
    return (_jsx(Box, { component: "button", type: "button", onClick: () => onChange(!checked), sx: {
            width: 36,
            height: 20,
            borderRadius: '10px',
            border: 'none',
            cursor: 'pointer',
            bgcolor: checked ? colorTokens.info : colorTokens.gray300,
            position: 'relative',
            flexShrink: 0,
            transition: 'background 0.2s',
            p: 0,
        }, children: _jsx(Box, { sx: {
                position: 'absolute',
                top: 3,
                left: checked ? 19 : 3,
                width: 14,
                height: 14,
                borderRadius: '50%',
                bgcolor: 'white',
                transition: 'left 0.2s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            } }) }));
};
export const CultureModal = memo((props) => {
    const { open, mode, pond, onClose, onSaved } = props;
    const { t } = useTranslation('ponds');
    const [pondName, setPondName] = useState(pond.name);
    const [pondSize, setPondSize] = useState(pond.areaHectares?.toString() ?? '');
    const [stockingDate, setStockingDate] = useState('');
    const [plsStocked, setPlsStocked] = useState('');
    const [initialAbw, setInitialAbw] = useState('0.1');
    const [aeratorHp, setAeratorHp] = useState('');
    const [hatcheryName, setHatcheryName] = useState('');
    const [species, setSpecies] = useState('Vannamei');
    const [isTestPond, setIsTestPond] = useState(false);
    const [alertsEnabled, setAlertsEnabled] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    /* Reset form when pond or open changes */
    useEffect(() => {
        if (open) {
            setPondName(pond.name);
            setPondSize(pond.areaHectares?.toString() ?? '');
            if (mode === 'edit') {
                // Pre-populate culture fields from PondListItem
                const rawDate = pond.cultivationDate;
                setStockingDate(rawDate ? new Date(rawDate).toISOString().split('T')[0] : '');
                setPlsStocked(pond.plStocked ? pond.plStocked.toString() : '');
                setInitialAbw('0.1');
                setAeratorHp(pond.aeratorHp ? pond.aeratorHp.toString() : '');
                setHatcheryName(pond.hatcheryName ?? '');
                setSpecies(pond.speciesType ?? 'Vannamei');
                setIsTestPond(pond.isTest ?? false);
                setAlertsEnabled(!(pond.pauseAlerts ?? false));
            }
            else {
                setStockingDate('');
                setPlsStocked('');
                setInitialAbw('0.1');
                setAeratorHp('');
                setHatcheryName('');
                setSpecies('Vannamei');
                setIsTestPond(false);
                setAlertsEnabled(true);
            }
            setError(null);
        }
    }, [open, pond, mode]);
    const handleClose = () => {
        setError(null);
        onClose();
    };
    const handleSave = async () => {
        setIsSaving(true);
        setError(null);
        try {
            if (mode === 'activate') {
                if (!stockingDate || !plsStocked) {
                    setError('activation.missingFields');
                    return;
                }
                await activatePond(pond.id, {
                    stockingDate,
                    plsStocked: parseInt(plsStocked, 10),
                    initialAbw: initialAbw ? parseFloat(initialAbw) : undefined,
                    aeratorHp: aeratorHp ? parseFloat(aeratorHp) : undefined,
                    hatcheryName: hatcheryName || undefined,
                    speciesType: species,
                    pondSize: pondSize ? parseFloat(pondSize) : undefined,
                });
            }
            else {
                // Edit mode — build update payload from changed fields
                const updates = {};
                if (pondName && pondName !== pond.name)
                    updates.name = pondName;
                if (pondSize)
                    updates.areaHectares = parseFloat(pondSize);
                if (stockingDate)
                    updates.cultivationDate = stockingDate;
                if (plsStocked)
                    updates.plStocked = parseInt(plsStocked, 10);
                if (aeratorHp)
                    updates.aeratorHp = parseFloat(aeratorHp);
                if (hatcheryName)
                    updates.hatcheryName = hatcheryName;
                updates.speciesType = species;
                if (Object.keys(updates).length > 0) {
                    await updatePond(pond.id, updates);
                }
                // Conditionally toggle test pond (only if changed)
                const currentIsTest = pond.isTest ?? false;
                if (isTestPond !== currentIsTest) {
                    await toggleTestPond(pond.id, isTestPond);
                }
                // Conditionally toggle alerts (only if changed)
                const currentPauseAlerts = pond.pauseAlerts ?? false;
                const newPauseAlerts = !alertsEnabled;
                if (newPauseAlerts !== currentPauseAlerts) {
                    await toggleAlerts(pond.id, newPauseAlerts);
                }
            }
            await onSaved();
            handleClose();
        }
        catch (err) {
            const errCode = err.errorCode ?? '';
            setError(errCode === 'DUPLICATE_PONDS'
                ? 'errors.duplicateName'
                : errCode === 'FORBIDDEN'
                    ? 'errors.permissionDenied'
                    : 'errors.saveFailed');
        }
        finally {
            setIsSaving(false);
        }
    };
    const isActivate = mode === 'activate';
    const canSave = isActivate ? Boolean(stockingDate && plsStocked && !isSaving) : !isSaving;
    return (_jsxs(Dialog, { open: open, onClose: handleClose, maxWidth: false, PaperProps: {
            sx: {
                width: 460,
                maxHeight: isActivate ? '92vh' : 'none',
                borderRadius: '16px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.paper',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 25px 50px -12px rgba(0, 0, 0, 0.12)',
            },
        }, children: [_jsxs(Box, { sx: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2.5,
                    py: 1.75,
                    bgcolor: colorTokens.gray50,
                    borderBottom: `1px solid ${colorTokens.gray200}`,
                }, children: [_jsxs(Box, { children: [_jsx(Typography, { sx: {
                                    fontSize: 13,
                                    fontWeight: 800,
                                    color: colorTokens.primary,
                                }, children: isActivate ? t('activation.title') : t('form.editTitle') }), _jsx(Typography, { sx: { fontSize: 11, color: colorTokens.gray600, mt: 0.25 }, children: pond.name })] }), _jsx(Box, { component: "button", onClick: handleClose, sx: {
                            p: 0.75,
                            border: 'none',
                            bgcolor: 'transparent',
                            cursor: 'pointer',
                            borderRadius: 1,
                            display: 'flex',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' },
                        }, children: _jsx(Typography, { sx: { fontSize: 15, color: colorTokens.gray600, lineHeight: 1 }, children: "\u2715" }) })] }), _jsxs(Box, { sx: {
                    flex: 1,
                    overflowY: isActivate ? 'auto' : 'visible',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    p: 2.5,
                }, children: [_jsxs(FieldRow, { children: [_jsx(Field, { label: "Pond Name", children: _jsx(Box, { component: "input", value: pondName, onChange: (e) => setPondName(e.target.value), disabled: isActivate, sx: inputSx }) }), _jsx(Field, { label: "Pond Size (ha)", children: _jsx(Box, { component: "input", type: "number", value: pondSize, onChange: (e) => setPondSize(e.target.value), min: 0.01, step: 0.01, sx: inputSx }) })] }), _jsxs(_Fragment, { children: [_jsxs(FieldRow, { children: [_jsx(Field, { label: t('activation.stockingDate'), required: isActivate, children: _jsx(Box, { component: "input", type: "date", value: stockingDate, onChange: (e) => setStockingDate(e.target.value), sx: inputSx }) }), _jsx(Field, { label: t('activation.plsStocked'), required: isActivate, children: _jsx(Box, { component: "input", type: "number", value: plsStocked, onChange: (e) => setPlsStocked(e.target.value), min: 0, placeholder: "e.g. 500000", sx: inputSx }) })] }), _jsxs(FieldRow, { children: [_jsx(Field, { label: t('activation.initialAbw'), children: _jsx(Box, { component: "input", type: "number", value: initialAbw, onChange: (e) => setInitialAbw(e.target.value), placeholder: "g", min: 0, step: 0.01, sx: inputSx }) }), _jsx(Field, { label: t('activation.aeratorHp'), children: _jsx(Box, { component: "input", type: "number", value: aeratorHp, onChange: (e) => setAeratorHp(e.target.value), placeholder: "HP", min: 0, step: 0.5, sx: inputSx }) })] }), _jsxs(FieldRow, { children: [_jsx(Field, { label: t('activation.hatcheryName'), children: _jsx(Box, { component: "input", value: hatcheryName, onChange: (e) => setHatcheryName(e.target.value), placeholder: t('activation.hatcheryPlaceholder'), sx: inputSx }) }), _jsx(Field, { label: t('activation.speciesType'), children: _jsx(Box, { component: "select", value: species, onChange: (e) => setSpecies(e.target.value), sx: { ...inputSx, appearance: 'auto' }, children: SPECIES.map((s) => (_jsx("option", { value: s, children: s }, s))) }) })] })] }), _jsxs(Box, { children: [_jsx(Typography, { sx: {
                                    fontSize: 10,
                                    fontWeight: 800,
                                    color: colorTokens.gray600,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.07em',
                                    mb: 1,
                                }, children: "Pond Settings" }), _jsxs(Box, { sx: {
                                    borderRadius: '10px',
                                    border: `1px solid ${colorTokens.gray200}`,
                                    overflow: 'hidden',
                                }, children: [_jsxs(Box, { sx: {
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            px: 1.75,
                                            py: 1.375,
                                            bgcolor: colorTokens.gray50,
                                        }, children: [_jsxs(Box, { sx: { pr: 1.5 }, children: [_jsx(Typography, { sx: { fontSize: 12, fontWeight: 700, color: colorTokens.gray800 }, children: t('activation.testPond') }), _jsx(Typography, { sx: { fontSize: 11, color: colorTokens.gray700, mt: 0.25 }, children: t('activation.testPondSub') })] }), _jsx(Toggle, { checked: isTestPond, onChange: setIsTestPond })] }), _jsx(Box, { sx: { height: 1, bgcolor: colorTokens.gray200 } }), _jsxs(Box, { sx: {
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            px: 1.75,
                                            py: 1.375,
                                            bgcolor: colorTokens.gray50,
                                        }, children: [_jsxs(Box, { sx: { pr: 1.5 }, children: [_jsx(Typography, { sx: { fontSize: 12, fontWeight: 700, color: colorTokens.gray800 }, children: t('activation.enableAlerts') }), _jsx(Typography, { sx: { fontSize: 11, color: colorTokens.gray700, mt: 0.25 }, children: t('activation.enableAlertsSub') })] }), _jsx(Toggle, { checked: alertsEnabled, onChange: setAlertsEnabled })] })] })] }), error && (_jsx(Box, { sx: {
                            p: 1.5,
                            borderRadius: '10px',
                            bgcolor: `${colorTokens.error}10`,
                            border: `1px solid ${colorTokens.error}30`,
                        }, children: _jsx(Typography, { sx: { fontSize: 12, color: colorTokens.error }, children: t(error) }) }))] }), _jsxs(Box, { sx: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 1.5,
                    px: 2.5,
                    py: 2,
                    borderTop: `1px solid ${colorTokens.gray200}`,
                    bgcolor: colorTokens.gray50,
                }, children: [_jsx(Box, { component: "button", onClick: handleClose, sx: {
                            px: 2,
                            py: 1,
                            borderRadius: '10px',
                            border: `1px solid ${colorTokens.gray200}`,
                            bgcolor: 'white',
                            color: colorTokens.gray600,
                            fontSize: 12,
                            fontWeight: 600,
                            lineHeight: 1.2,
                            cursor: 'pointer',
                        }, children: t('common.cancel') }), _jsxs(Box, { component: "button", onClick: handleSave, disabled: !canSave, sx: {
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 2.5,
                            py: 1,
                            borderRadius: '10px',
                            border: 'none',
                            bgcolor: canSave ? colorTokens.primary : colorTokens.gray300,
                            color: 'white',
                            fontSize: 12,
                            fontWeight: 600,
                            lineHeight: 1.2,
                            cursor: canSave ? 'pointer' : 'not-allowed',
                        }, children: [isSaving && _jsx(CircularProgress, { size: 11, sx: { color: 'white' } }), isActivate ? t('activation.activateButton') : t('form.saveChanges')] })] })] }));
});
